import { Component, Input } from '@angular/core';
import { calculateTimeRemainingToDate } from '../utils/helpers';
import { CommonService } from 'src/app/core/services/common.service';

export interface FCRARecord {
  fcraRegistrationNumber: string;
  associationName: string;
  associationAddress: string;
  sourceState: string;
  pinCode: string;
  validityFromDate: string;
  validityToDate: string;
  lastRenewedDate: string;
}

interface ValidityDetails {
  status: {
    className: string;
  };
}

interface TimeRemaining {
  text: string;
  months: number;
  isExpired: boolean;
}

@Component({
  selector: 'app-fcra-compliance-card',
  templateUrl: './fcra-compliance-card.component.html',
  styleUrls: ['./fcra-compliance-card.component.scss']
})
export class FcraComplianceCardComponent {
  @Input() fcraMatches: FCRARecord[] = [];
  @Input() syncDate?: string;

  constructor(private commonService: CommonService) { }

  get fcraRecord(): FCRARecord | null {
    return this.fcraMatches?.[0] || null;
  }

  get hasActiveFCRA(): boolean {
    return this.fcraMatches?.length > 0;
  }

  get validityDetails(): ValidityDetails | null {
    if (!this.fcraRecord?.validityToDate) {
      return null;
    }
    return this.getValidityDisplay(this.fcraRecord.validityToDate);
  }

  get timeRemaining(): TimeRemaining | null {
    if (!this.fcraRecord?.validityToDate) {
      return null;
    }
    return calculateTimeRemainingToDate(this.fcraRecord.validityToDate);
  }

  get formattedSyncDate(): string {
    if (!this.syncDate) {
      return '';
    }
    return this.formatDate(this.syncDate);
  }

  getValidityDisplay(validityToDate: string): ValidityDetails {
    const timeRemaining = calculateTimeRemainingToDate(validityToDate);

    let className = 'bg-gray-100 text-gray-800';
    if (timeRemaining.isExpired) {
      className = 'bg-red-100 text-red-800';
    } else if (timeRemaining.months < 1) {
      className = 'bg-red-100 text-red-800';
    } else if (timeRemaining.months < 6) {
      className = 'bg-orange-100 text-orange-800';
    } else if (timeRemaining.months < 12) {
      className = 'bg-yellow-100 text-yellow-800';
    } else {
      className = 'bg-green-100 text-green-800';
    }

    return {
      status: {
        className
      }
    };
  }


  get alertClass(): string {
    if (!this.timeRemaining) {
      return '';
    }
    if (this.timeRemaining.isExpired) {
      return 'bg-red-50 text-red-800';
    }
    if (this.timeRemaining.months < 1) {
      return 'bg-red-50 text-red-800';
    }
    if (this.timeRemaining.months < 6) {
      return 'bg-orange-50 text-orange-800';
    }
    return 'bg-yellow-50 text-yellow-800';
  }

  get alertMessage(): string {
    if (!this.timeRemaining) {
      return '';
    }
    if (this.timeRemaining.isExpired) {
      return 'EXPIRED: FCRA registration has expired. Immediate action required to restore foreign funding eligibility.';
    }
    if (this.timeRemaining.months < 1) {
      return 'URGENT ALERT: FCRA registration expires soon. Immediate renewal action required to maintain foreign funding eligibility.';
    }
    if (this.timeRemaining.months < 6) {
      return 'WARNING: FCRA registration expires soon. Plan renewal process to maintain foreign funding eligibility.';
    }
    return 'NOTICE: FCRA registration expires soon. Consider renewal timeline to maintain foreign funding eligibility.';
  }

  formatDate(date: string): string {
    if (!date) return 'N/A';
    try {
      const dateObj = new Date(date);
      return this.commonService.dateFormatter(dateObj.toISOString());
    } catch {
      return "N/A";
    }
  }
}

